import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseTableOrderField, RemoteType } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createIconCellFormatter,
  createLabelColumnFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  GraphLabel,
  GraphQLError,
  GridSortProps,
  LabelContent,
  RemoteTypeMappings,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  SiteUrlsContext,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { GlobalAssetGridDC3ColumnFactoryParams } from '../sorting/makeColumnFactoryParams';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';

export interface VirtualConnectionTableColumnsGridRowData {
  readonly virtualConnectionTableId: string;
  readonly id: string;
  readonly name: string | null;
  readonly remoteType: RemoteType;
  readonly labels?: ReadonlyArray<GraphLabel>;
  readonly maxDownstreamDatasources?: number | null;
  readonly maxDownstreamWorkbooks?: number | null;
}

type VirtualConnectionTableColumnsColumnFactory = ColumnFactory<
  GlobalAssetGridDC3ColumnFactoryParams<TablesValidSortFields>,
  VirtualConnectionTableColumnsGridRowData,
  TablesValidSortFields
>;

function columns(siteUrls: SiteUrls): ReadonlyArray<VirtualConnectionTableColumnsColumnFactory> {
  const columns: ReadonlyArray<VirtualConnectionTableColumnsColumnFactory> = [
    () => createSelectionColumn(),
    (sortParams) => ({
      name: 'type',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        ({ remoteType }) => <RemoteTypeIcon remoteType={remoteType} />,
        ({ remoteType }) => RemoteTypeMappings[remoteType](),
      ),
      resizable: false,
      sortable: false,
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (sortParams) => ({
      name: 'name',
      dataKey: DatabaseTableOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createQueryPreservingReplacingLinkCellFormatter<VirtualConnectionTableColumnsGridRowData>(
        ({ name }) => name || '',
        (urls, { virtualConnectionTableId, id }) => urls.virtualConnectionTableLineageFieldPath(virtualConnectionTableId, id),
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'workbooks',
      dataKey: DatabaseTableOrderField.DOWNSTREAM_WORKBOOKS_COUNT,
      headerText: Messages.GridHeaderWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createNumberCellFormatter(
        ({ maxDownstreamWorkbooks }) =>
          maxDownstreamWorkbooks
            ? maxDownstreamWorkbooks
            : null
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'datasources',
      dataKey: DatabaseTableOrderField.DOWNSTREAM_DATASOURCES_COUNT,
      headerText: Messages.GridHeaderDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createNumberCellFormatter(
        ({ maxDownstreamDatasources }) =>
          maxDownstreamDatasources
            ? maxDownstreamDatasources
            : null
      ),
      sortable: true,
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'SENSITIVITY'),
  ];

  return columns.filter(Boolean) as ReadonlyArray<VirtualConnectionTableColumnsColumnFactory>;
}

export type VirtualConnectionTableColumnsDisplayGridProps =
  UsualDisplayGridProps<VirtualConnectionTableColumnsGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<TablesValidSortFields>;

export function VirtualConnectionTableColumnsDisplayGrid(props: VirtualConnectionTableColumnsDisplayGridProps): JSX.Element {
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls)
    .map(columnFactory => columnFactory(props))
    .filter(column => {
      if ((column.name === 'label-sensitivity' || column.name === 'label-warning') && !customSensitivityLabelsEnabled) {
        return false;
      }
      return true;
  });

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

function getLabelContentFromRowData(rowData: VirtualConnectionTableColumnsGridRowData, urls: SiteUrls): LabelContent {
  const contentParams: LabelContent = {
    contentId: rowData.id,
    contentType: null,
    contentName: rowData.name ?? undefined,
    subjectArea: 'Column',
    contentUrl: urls.virtualConnectionTableLineageFieldUrl(rowData.virtualConnectionTableId, rowData.id),
  };
  return contentParams;
}

function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']): TypedColumn<VirtualConnectionTableColumnsGridRowData, TablesValidSortFields> {

  function getPropsFromRowData(rowData: VirtualConnectionTableColumnsGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData, urls);
    const labels: ReadonlyArray<GraphLabel> = rowData.labels ? rowData.labels : [];
    return getLabelParamsForGrids({ category, labels, urls, source: 'vconn-columns', content });
  }

  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<VirtualConnectionTableColumnsGridRowData, TablesValidSortFields>;
}
